import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const UnderstandingHomepage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Getting Started
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Understanding the Homepage
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          The IndeeClips homepage is your personalized content hub. Learn how to navigate the feed, discover new videos, and customize your viewing experience.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Homepage Layout
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          The homepage consists of several key sections:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Top Navigation Bar:</strong> Logo, search, profile icon, upload button</li>
          <li><strong>Sidebar (Desktop):</strong> Home, Subscriptions, Clips, Profile, More options</li>
          <li><strong>Bottom Navigation (Mobile):</strong> Same features as sidebar, optimized for touch</li>
          <li><strong>Main Feed:</strong> Grid of recommended videos tailored to your interests</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How the Feed Works
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Your homepage feed is personalized based on:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Watch history:</strong> Videos you've watched recently</li>
          <li><strong>Subscriptions:</strong> New uploads from channels you follow</li>
          <li><strong>Likes and engagement:</strong> Videos you've interacted with</li>
          <li><strong>Search history:</strong> Topics you've searched for</li>
          <li><strong>Trending content:</strong> Popular videos in India</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Video Card Elements
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Each video in the feed shows:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Thumbnail:</strong> Preview image representing the video</li>
          <li><strong>Duration:</strong> Video length displayed in bottom-right of thumbnail</li>
          <li><strong>Title:</strong> Video name (click to watch)</li>
          <li><strong>Channel name:</strong> Creator who uploaded the video</li>
          <li><strong>View count:</strong> Number of times the video has been watched</li>
          <li><strong>Upload time:</strong> How long ago the video was published</li>
          <li><strong>Three-dot menu:</strong> Additional options (Save, Not interested, Report)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Sidebar Navigation
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Home:</strong> Personalized feed (default view)</li>
          <li><strong>Subscriptions:</strong> Latest videos from channels you subscribe to</li>
          <li><strong>Clips:</strong> Short-form vertical videos (like Shorts)</li>
          <li><strong>Profile:</strong> Your account settings and watch history</li>
          <li><strong>More:</strong> Help Center, FAQ, About, Contact, Settings</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Customizing Your Feed
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
          "Not Interested" Feature
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Click three-dot menu on any video</li>
          <li>Select "Not Interested"</li>
          <li>Algorithm shows fewer similar videos</li>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, mt: 3, fontWeight: 600 }}>
          "Don't Recommend Channel"
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Click three-dot menu</li>
          <li>Select "Don't Recommend Channel"</li>
          <li>Videos from that channel won't appear in your feed</li>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, mt: 3, fontWeight: 600 }}>
          Clear Watch History (Reset Recommendations)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Settings → Privacy → Clear Watch History. This resets your recommendations to a fresh start.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Mobile vs Desktop Experience
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          <strong>Mobile:</strong>
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Bottom navigation bar for easy thumb access</li>
          <li>Swipe gestures supported (swipe right to open sidebar)</li>
          <li>Vertical scrolling feed optimized for small screens</li>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          <strong>Desktop:</strong>
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Persistent left sidebar for navigation</li>
          <li>Grid layout showing more videos per screen</li>
          <li>Hover previews (coming soon) for quick video glimpses</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Refreshing the Feed
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Desktop:</strong> Click IndeeClips logo or Home button</li>
          <li><strong>Mobile:</strong> Pull down from top of feed to refresh</li>
          <li><strong>Automatic:</strong> Feed updates in background as you scroll</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Tips for Better Recommendations
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Watch videos to completion—algorithm learns your preferences</li>
          <li>Like videos you enjoy—positive feedback signal</li>
          <li>Subscribe to channels you love—get more of their content</li>
          <li>Use "Not Interested" liberally—teach algorithm what to avoid</li>
          <li>Search for topics you're interested in—discovery improves over time</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Still Confused?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Need help navigating IndeeClips? Contact{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default UnderstandingHomepage;
